import React, { forwardRef } from 'react';
import { ReactThreeFiber, useThree } from 'react-three-fiber';
import { Text } from 'drei';
import { PUBLIC_URL_ROOT } from '../../../config/secrets';
import { isIOS } from '../../../common/device';
import { splitTextBySlides } from '../../../common/utils';


const fontWeblysleek = `${PUBLIC_URL_ROOT}assets/fonts/weblysleek/weblysleekui_normal.ttf`;
const fontRoboto = `${PUBLIC_URL_ROOT}assets/fonts/roboto/roboto.regular.ttf`;

const fontFamily = `${PUBLIC_URL_ROOT}assets/fonts/microsoft-yahei/chinese.msyh.ttf`;

type LinkType = {
  link:string;
  text:string;
  spaces: number;
  color: string;
  underline_text: string;
  underline_color: string;
}

type TTextProps = {
  value: string;
  highlightValue?: string;
  linkValue?: string;
  links: LinkType[];
  activeSlideIndex: number;
  lastSlideIndex: number
};

const TitleText = forwardRef(({ value, highlightValue }: TTextProps, ref) => {
  const fontSize = 0.05;
  const textAlign = 'center';
  const anchorX = 'center';
  const anchorY = 'middle';
  const color = '#ffffff';
  const position: ReactThreeFiber.Vector3 = [0, 0, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

const BigText = forwardRef(({ value, highlightValue, linkValue }: TTextProps, ref) => {
  const fontSize = 0.028;
  const color = '#ffffff';
  const textAlign = 'left';
  const anchorX = 'left';
  const anchorY = 'top';
  const position: ReactThreeFiber.Vector3 = [-0.24, 0.2, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

const wrap = (s:string, w:number) => s.replace(
  new RegExp(`(?![^\\n]{1,${w}}$)([^\\n]{1,${w}})\\s`, 'g'), '$1\n'
);

const getLinksForSlide = (links: LinkType[], activeSlideIndex:number)=>{
  return links.filter((link )=> {
    let lowerBound = 90 * 5 * (activeSlideIndex - 1);
    let upperBound = 90 * 5 * (activeSlideIndex);
    console.log({ lowerBound, upperBound, spaces: link.spaces })
    return !(link.spaces < lowerBound || link.spaces >= upperBound);

  });
}

const DefaultText = forwardRef(({ value, highlightValue, linkValue, links,lastSlideIndex, activeSlideIndex }: TTextProps, ref) => {
  const fontSize = 0.02;
  const color = '#ffffff';
  const textAlign = 'left';
  const anchorX = 'left';
  const anchorY = 'top';
  const position: ReactThreeFiber.Vector3 = [-0.24, -0.1, 0.001];
  const { gl } = useThree();

  let linksCurrentSlide = getLinksForSlide(links,activeSlideIndex);
  if(activeSlideIndex > lastSlideIndex){
    linksCurrentSlide = getLinksForSlide(links,lastSlideIndex);
  }

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>

      {linksCurrentSlide.map((link )=>  {
        return <><Text
          onClick={()=>{
            window.open(link.link)
          }}
          onPointerOver={()=>{
            gl.domElement.classList.add('cursor-pointer');
          }}
          onPointerOut={()=>{
            gl.domElement.classList.remove('cursor-pointer');
          }
          }
          renderOrder={4}
          color={link.color}
          font={fontFamily}
          fontSize={fontSize}
          maxWidth={0.48}
          lineHeight={1.2}
          textAlign={textAlign}
          anchorX={anchorX}
          anchorY={anchorY}>
          {wrap(link.text,90)}
        </Text>
          <Text
          onClick={()=>{
            window.open(link.link)
          }}
          renderOrder={4}
          color={link.underline_color}
          font={fontFamily}
          fontSize={fontSize}
          maxWidth={0.48}
          lineHeight={1.2}
          textAlign={textAlign}
          anchorX={anchorX}
          anchorY={anchorY}>
          {wrap(link.underline_text,90)}
        </Text>
        </>
        }
      )}
    </group>
  );
});

type TStoryTextProps = TTextProps & {
  isBig?: boolean;
  isTitle?: boolean;
};

const StoryText = forwardRef(({ value, highlightValue = '', linkValue = '', isTitle, isBig, links = [], lastSlideIndex, activeSlideIndex }: TStoryTextProps, ref) => {
  let Component = DefaultText;

  if (isTitle) {
    Component = TitleText;
  } else if (isBig) {
    Component = BigText;
  }

  return <Component links={links} value={value} lastSlideIndex={lastSlideIndex} highlightValue={highlightValue} activeSlideIndex={activeSlideIndex} linkValue={linkValue} ref={ref} />;
});

export default StoryText;
